import React from 'react';

import { Text } from '@change/ds-core-ui';

import { translationKey } from '@change-corgi/core/i18n';
import type { TranslationKey } from '@change-corgi/core/i18n';
import { localizeNumber } from '@change-corgi/core/intl';

import { useSharingTools } from '../../../../hooks/useSharingTools';
import { PLATFORM_CONFIG } from '../../../../shared/constants';
import { PlatformIcon } from '../../../common/PlatformIcon';
import { SharingToolsCardWrapperVariant1 } from '../../../SharingToolsCardWrapper';
import type { GroupData } from '../RecommendedGroupCardsContainer/types';
import { RecommendedGroupsCardLoading } from '../RecommendedGroupsCardLoading';

/**
 * Displays a preview card for recommended groups with platform-specific styling.
 * Shows a loading state when no group data is provided.
 *
 * ```tsx
 * <RecommendedGroupsCardPreviewVariant1
 * />
 * ```
 */
export const RecommendedGroupsCardPreviewVariant1: React.FC<Props> = ({
	titleKey = translationKey('corgi.petition.starter_dashboard_v2.sharing_tools.recommended_groups.title_variant1'),
	descriptionKey = translationKey(
		'corgi.petition.starter_dashboard_v2.sharing_tools.recommended_groups.description_variant1',
	),
}) => {
	const { data, actions } = useSharingTools('petition_dashboard_grow_petition_v1');

	const recommendedGroups = data.recommendedGroups.groups;

	if (recommendedGroups.length === 0 || data.recommendedGroups.isLoading) {
		return <RecommendedGroupsCardLoading title={titleKey} description={descriptionKey} />;
	}

	const {
		group: {
			name,
			metadata: { memberCount: followers, imageUrl: communityIconUrl },
		},
		platform,
	} = recommendedGroups[0];

	const { bgColor } = PLATFORM_CONFIG[platform];

	return (
		<SharingToolsCardWrapperVariant1
			title={titleKey}
			description={descriptionKey}
			primaryActionOnClick={actions.navigateToGroupRecommendations}
		>
			<article
				className="h-[174px] w-[144.12px] overflow-visible rounded-xl bg-white shadow-sm"
				aria-label={`${platform} group: ${name}`}
			>
				<figure
					className="relative flex h-24 items-center justify-center overflow-visible rounded-t-xl border-0 border-solid border-white bg-fill-weak bg-cover bg-center bg-no-repeat"
					style={{ backgroundColor: bgColor }}
				>
					<PlatformIcon platform={platform} />

					{communityIconUrl ? (
						<div
							className="bg-fill-base aspect-square h-[67px] w-[67px] rounded-full bg-contain bg-center bg-no-repeat shadow"
							style={{
								backgroundImage: `url(${communityIconUrl})`,
								imageRendering: 'crisp-edges',
							}}
							role="img"
							aria-label={`${name} logo`}
						/>
					) : (
						<div className="h-14 w-14 rounded-full border-4 border-white shadow" aria-hidden="true">
							<div className="to-fill-error h-full w-full rounded-full bg-gradient-to-b from-transparent" />
						</div>
					)}
				</figure>

				<section className="bg flex w-36 flex-col items-start justify-center rounded-b-xl p-3">
					<Text
						as="h4"
						variant="heading"
						size="xx-small"
						className="w-full max-w-full overflow-hidden truncate whitespace-nowrap text-typography-strong"
					>
						{name}
					</Text>
					{followers !== null && (
						<Text as="p" variant="body" size="x-small" className="text-typography-weak">
							{localizeNumber('en', followers)} followers
						</Text>
					)}
				</section>
			</article>
		</SharingToolsCardWrapperVariant1>
	);
};

type Props = Readonly<{
	group?: GroupData;
	titleKey?: TranslationKey;
	descriptionKey?: TranslationKey;
}>;
